import { PoweredBy } from '@/components/print/PoweredBy';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { getPrintableFbrInvoiceId, isNonFbrInvoiceId } from '@shared/types/fbr-offline';
import { Printer, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog } from 'primereact/dialog';
import { posService } from '@/services/pos.service';
import { formatCurrency, formatDate, formatMoney2, formatRate } from '@/lib/utils';
import { convertPkrToCurrency } from '@/lib/currency-convert';
import { paymentMethodLabel } from '@/lib/payment-labels';
import { serialReceiptLines } from '@/lib/receipt-serial-lines';
import { aggregateTaxBreakdown, formatTaxLabel, normalizeTaxBreakdown } from '@/lib/tax';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { ReceiptIdentity, ReceiptFooterText, ReceiptReturnPolicy, ReceiptCashierLine } from './ReceiptParts';
import { resolveReceiptLayout } from '@/types/pos';
import { getOfflineSale } from '@/lib/offline-db';

interface Props {
  saleId: string | null;
  onClose: () => void;
}

// Shape of the offline cart snapshot persisted in IndexedDB (best-effort; all optional).
// It is the cart line as the till held it (CartItem), plus what the customer was charged.
interface OfflineSnapshotItem {
  product?: { name?: string };
  variant?: { name?: string } | null;
  quantity?: number;
  unitPrice?: number;
  discountAmount?: number;
  discountType?: 'PERCENTAGE' | 'FIXED' | null;
  discountValue?: number;
}
interface OfflineSnapshotPayment {
  method?: string;
  amount?: number;
}
interface OfflineSnapshot {
  selectedLocation?: { name?: string };
  localInvoiceNumber?: string;
  createdAt?: string | number;
  customer?: { name?: string; ntn?: string | null; cnic?: string | null; isOnAtl?: boolean } | null;
  items?: OfflineSnapshotItem[];
  payments?: OfflineSnapshotPayment[];
  subtotal?: number;
  totalTax?: number;
  orderDiscountAmount?: number;
  grandTotal?: number;
  /** POS-I62: the figures the customer was actually charged (same as the Payment dialog). */
  promotions?: Array<{ name: string; discountAmount: number }>;
  promotionDiscount?: number;
  furtherTaxAmount?: number;
  taxBreakdown?: Array<{ name: string; rate: number; type: string; amount: number }>;
  tipAmount?: number;
  netTotal?: number;
  changeAmount?: number;
}

/** POS-I115: the paper roll (58 or 80 mm) chosen under Settings > Printer; @page cannot follow a class, so
 *  the page size rule is written into one <style> element just before printing. */
function applyReceiptPageSize() {
  let width: 58 | 80 = 80;
  try {
    const raw = localStorage.getItem('pos-printer-settings');
    if (raw && JSON.parse(raw).paperWidth === 58) width = 58;
  } catch {
    // keep 80 mm
  }
  let el = document.getElementById('receipt-page-size');
  if (!el) {
    el = document.createElement('style');
    el.id = 'receipt-page-size';
    document.head.appendChild(el);
  }
  el.textContent = `@media print { @page { size: ${width}mm auto; margin: 0; } #receipt { width: ${width}mm !important; --rf: ${width === 58 ? 0.8 : 1}; } }`;
}

/** POS-I229: DD-MMM-YYYY in the shop's time zone. */
function formatBackfillDate(iso: string, timezone?: string | null): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: timezone || 'Asia/Karachi',
    }).formatToParts(new Date(iso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('day')}-${get('month')}-${get('year')}`;
  } catch {
    return iso.slice(0, 10);
  }
}

export function ReceiptView({ saleId, onClose }: Props) {
  const { hasPermission } = useAuthStore();
  // POS-I114: the user's fbr:read permission only says what they may OPEN. Whether the receipt may carry
  // an FBR number / QR depends on the shop: FBR live and a real FBR-issued id (see `fbrLive` below).
  const canUseFbr = hasPermission('fbr:*') || hasPermission('fbr:read');
  const { t } = useTranslation('receipt');

  // Detect offline sale: check IndexedDB for this saleId
  const [offlineSnapshot, setOfflineSnapshot] = useState<OfflineSnapshot | null>(null);
  const [offlineChecked, setOfflineChecked] = useState(false);

  useEffect(() => {
    if (!saleId) { setOfflineSnapshot(null); setOfflineChecked(true); return; }
    getOfflineSale(saleId).then((rec) => {
      if (rec) setOfflineSnapshot((rec.cartSnapshot ?? null) as OfflineSnapshot | null);
      setOfflineChecked(true);
    });
  }, [saleId]);

  const isOffline = offlineChecked && !!offlineSnapshot;

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['receipt', saleId],
    queryFn: () => posService.getReceipt(saleId!),
    enabled: !!saleId && offlineChecked && !isOffline,
  });

  // Poll for FBR invoice ID for up to ~12 seconds after the receipt loads
  const [fbrPollingActive, setFbrPollingActive] = useState(false);
  const [fbrInvoiceId, setFbrInvoiceId] = useState<string | null>(null);
  const [fbrPollTimeout, setFbrPollTimeout] = useState(false);

  const fbrLive = receipt?.tenant?.fbrLive === true;

  useEffect(() => {
    if (!saleId || !canUseFbr || !fbrLive) { setFbrInvoiceId(null); setFbrPollingActive(false); setFbrPollTimeout(false); return; }
    // Start polling when receipt loads and FBR id is not yet available
    if (receipt && !receipt.fbrInvoiceId) {
      setFbrPollingActive(true);
      setFbrPollTimeout(false);
      const timeout = setTimeout(() => {
        setFbrPollingActive(false);
        setFbrPollTimeout(true);
      }, 20000);
      return () => clearTimeout(timeout);
    } else if (receipt?.fbrInvoiceId) {
      setFbrInvoiceId(receipt.fbrInvoiceId);
      setFbrPollingActive(false);
    }
  }, [saleId, receipt, canUseFbr, fbrLive]);

  // Poll the FBR submission for this sale while polling is active (online sales only)
  const { data: fbrData } = useQuery({
    queryKey: ['fbr-submission-receipt', saleId],
    queryFn: () => api.get(`/fbr/submissions?saleId=${saleId}&limit=1`).then((r) => r.data),
    enabled: !!saleId && fbrPollingActive && canUseFbr && fbrLive && !isOffline,
    refetchInterval: fbrPollingActive ? 2000 : false,
  });

  useEffect(() => {
    const submission = fbrData?.data?.[0];
    if (submission?.status === 'ACCEPTED' && submission?.fbrInvoiceId && !isNonFbrInvoiceId(submission.fbrInvoiceId)) {
      setFbrInvoiceId(submission.fbrInvoiceId);
      setFbrPollingActive(false);
    }
  }, [fbrData]);

  const resolvedFbrId = getPrintableFbrInvoiceId({
    fbrInvoiceId: fbrInvoiceId ?? receipt?.fbrInvoiceId ?? null,
    fbrEnabled: fbrLive,
    fbrEnvironment: fbrLive ? 'LIVE' : null,
  });
  const showFbrQr = canUseFbr && !!resolvedFbrId;

  // POS-I229: footer, logo (Receipt Customization logo, else the Branding logo: POS-I217), tagline and
  // layout come from the shop block frozen on the sale, so a reprint shows the shop of that day.
  // The offline cart-snapshot render below has no server receipt and does not use them.
  const receiptFooter = receipt?.tenant?.receiptFooter ?? '';
  const receiptLogoUrl = receipt?.tenant?.logoUrl ?? '';
  const brandTagline: string = receipt?.tenant?.tagline ?? '';
  // POS-I220: which sections print; an older response without a layout prints everything.
  const layout = resolveReceiptLayout(receipt?.tenant?.layout);

  const handlePrint = () => {
    applyReceiptPageSize();
    window.print();
  };

  const onAccountAmount = (receipt?.payments ?? [])
    .filter((p) => p.method === 'CREDIT')
    .reduce((sum, p) => sum + Number(p.amount), 0);
  // POS-I119: "You saved" = line (bargaining) + order + promotion discounts of the sale.
  // POS-I360: the currency line comes from the sale's own snapshot (any payment method); a sale without one (older
  // sales, offline receipts) falls back to the foreign cash tender's code and rate (POS-I359).
  const totalInLine =
    receipt?.receiptCurrencyCode && Number(receipt.receiptExchangeRate || 0) > 0
      ? { code: receipt.receiptCurrencyCode, rate: Number(receipt.receiptExchangeRate) }
      : receipt?.currencyCode && Number(receipt.exchangeRate || 0) > 0
        ? { code: receipt.currencyCode, rate: Number(receipt.exchangeRate) }
        : null;
  const youSaved = receipt
    ? Number(
        receipt.lineDiscountAmount ??
          receipt.items.reduce((sum, item) => sum + Number(item.discountAmount ?? 0), 0),
      ) +
      Number(receipt.discountAmount ?? 0) +
      (receipt.salePromotions ?? []).reduce((sum, sp) => sum + Number(sp.discountAmount ?? 0), 0)
    : 0;
  const receiptTaxBreakdown = receipt?.items
    ? aggregateTaxBreakdown(receipt.items.map((item) => item.taxBreakdown))
    : [];

  const footer = (
    <div className="flex gap-2 print:hidden">
      <Button variant="outline" onClick={onClose} className="flex-1">
        {t('close', { ns: 'common' })}
      </Button>
      <Button
        onClick={handlePrint}
        className="flex-1 bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground"
      >
        <Printer className="me-1 h-4 w-4" />
        {t('print', { ns: 'common' })}
      </Button>
    </div>
  );

  // ─── Offline receipt render (from cart snapshot) ──────────────────────────
  if (offlineChecked && isOffline && offlineSnapshot) {
    const snap = offlineSnapshot;
    const offlineTotal = snap.netTotal ?? snap.grandTotal ?? 0;
    const offlineYouSaved =
      (snap.items ?? []).reduce((sum, item) => sum + Number(item.discountAmount ?? 0), 0) +
      Number(snap.orderDiscountAmount ?? 0) +
      (snap.promotions ?? []).reduce((sum, promo) => sum + Number(promo.discountAmount ?? 0), 0);
    const offlinePaid = (snap.payments ?? []).reduce((sum, p) => sum + Number(p.amount ?? 0), 0);
    const offlineChange = snap.changeAmount ?? Math.max(0, offlinePaid - offlineTotal - Number(snap.tipAmount ?? 0));
    return (
      <Dialog
        visible={!!saleId}
        onHide={onClose}
        header={t('title')}
        footer={footer}
        style={{ width: '24rem' }}
        draggable={false}
        resizable={false}
        contentClassName="p-0"
        className="receipt-dialog"
        headerClassName="print:hidden"
      >
        <div className="p-4 text-foreground">
          <div id="receipt" className="space-y-4">
            {/* Pending sync badge */}
            <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-text print:hidden">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span>Pending sync — this receipt will be confirmed once back online.</span>
            </div>
            {/* Header */}
            <div className="text-center border-b pb-3">
              <h2 className="text-lg font-bold">Receipt</h2>
              {snap.selectedLocation?.name && (
                <p className="text-xs font-medium text-muted-foreground">{snap.selectedLocation.name}</p>
              )}
            </div>
            {/* Invoice info */}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Receipt #</span>
              <span className="font-medium">{snap.localInvoiceNumber}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Date</span>
              <span>{new Date(snap.createdAt ?? Date.now()).toLocaleString()}</span>
            </div>
            {snap.customer && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Customer</span>
                <span>{snap.customer.name}</span>
              </div>
            )}
            {snap.customer && (snap.customer.ntn || snap.customer.cnic) && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{snap.customer.ntn ? 'NTN' : 'CNIC'}</span>
                <span>{snap.customer.ntn || snap.customer.cnic}</span>
              </div>
            )}
            {/* Items (POS-I62: unit price and line value come from the cart line, not a missing field) */}
            <div className="border-t pt-2 space-y-1">
              {snap.items?.map((item, i: number) => {
                const unit = Number(item.unitPrice ?? 0);
                const qty = Number(item.quantity ?? 0);
                const lineDiscount = Number(item.discountAmount ?? 0);
                return (
                  <div key={i} className="text-xs">
                    <div className="flex justify-between gap-3">
                      <span>
                        {item.product?.name}{item.variant ? ` (${item.variant.name})` : ''}
                        <span className="text-muted-foreground ms-1">x{qty} @ {formatCurrency(unit)}</span>
                      </span>
                      <span>{formatCurrency(unit * qty)}</span>
                    </div>
                    {lineDiscount > 0 && (
                      <div className="flex justify-between gap-3 ps-2 text-[11px] text-success-text">
                        <span>Line discount{item.discountType === 'PERCENTAGE' ? ` (${Number(item.discountValue)}%)` : ''}</span>
                        <span>-{formatCurrency(lineDiscount)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Totals: the same lines the Payment dialog showed */}
            <div className="border-t pt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{formatCurrency(snap.subtotal ?? 0)}</span>
              </div>
              {(snap.orderDiscountAmount ?? 0) > 0 && (
                <div className="flex justify-between text-success-text">
                  <span>Discount</span>
                  <span>-{formatCurrency(snap.orderDiscountAmount ?? 0)}</span>
                </div>
              )}
              {(snap.promotions ?? []).map((promo, i: number) => (
                <div key={i} className="flex justify-between text-success-text">
                  <span className="truncate max-w-[60%]">🏷 {promo.name}</span>
                  <span>-{formatCurrency(promo.discountAmount)}</span>
                </div>
              ))}
              {(snap.totalTax ?? 0) > 0 && (
                <div className="flex justify-between">
                  <span>Tax</span>
                  <span>{formatCurrency(snap.totalTax ?? 0)}</span>
                </div>
              )}
              {normalizeTaxBreakdown(snap.taxBreakdown).map((tax, i: number) => (
                <div key={i} className="flex justify-between text-muted-foreground">
                  <span>{formatTaxLabel(tax)}</span>
                  <span>{formatCurrency(tax.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-sm border-t pt-1">
                <span>Total</span>
                <span>{formatCurrency(offlineTotal)}</span>
              </div>
              {offlineYouSaved > 0 && (
                <div className="flex justify-between text-xs font-medium text-success-text">
                  <span>You saved</span>
                  <span>{formatCurrency(offlineYouSaved)}</span>
                </div>
              )}
              {(snap.tipAmount ?? 0) > 0 && (
                <div className="flex justify-between font-medium">
                  <span>Tip / Gratuity</span>
                  <span>{formatCurrency(snap.tipAmount ?? 0)}</span>
                </div>
              )}
            </div>
            {/* Payments */}
            {(snap.payments?.length ?? 0) > 0 && (
              <div className="border-t pt-2 text-xs space-y-1">
                {(snap.payments ?? []).map((p, i: number) => (
                  <div key={i} className="flex justify-between text-muted-foreground">
                    <span>{paymentMethodLabel(p.method)}</span>
                    <span>{formatCurrency(p.amount ?? 0)}</span>
                  </div>
                ))}
                {offlineChange > 0 && (
                  <div className="flex justify-between font-semibold">
                    <span>Change</span>
                    <span>{formatCurrency(offlineChange)}</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-center text-[11px] text-muted-foreground">
              The FBR invoice number is issued when this sale syncs.
            </p>
            <p className="text-center text-xs text-muted-foreground pt-2">Thank you for your purchase!</p>
          </div>
        </div>
      </Dialog>
    );
  }

  // ─── Normal (online) receipt render ──────────────────────────────────────
  return (
    <Dialog
      visible={!!saleId}
      onHide={onClose}
      header={t('title')}
      footer={footer}
      style={{ width: '24rem' }}
      draggable={false}
      resizable={false}
      contentClassName="p-0"
      className="receipt-dialog"
      headerClassName="print:hidden"
    >
      <div className="p-4 text-foreground">
        {isLoading || !receipt ? (
          <div className="flex h-40 items-center justify-center">{t('loading')}</div>
        ) : (
          <div id="receipt" className="space-y-4">
            {/* Header */}
            <div className="text-center border-b pb-3">
              <ReceiptIdentity
                logoUrl={receiptLogoUrl}
                name={receipt.tenant?.name}
                tagline={brandTagline}
                ntn={receipt.tenant?.fbrNtn}
                location={receipt.location}
                layout={layout}
              />
              <p className="text-xs text-muted-foreground">{t('taxInvoice')}</p>
              <p className="receipt-invoice text-xs text-muted-foreground mt-1 whitespace-nowrap">
                {t('invoice')}: <bdi dir="ltr">{receipt.invoiceNumber}</bdi>
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDate(receipt.createdAt)}
              </p>
              {layout.showCashier && <ReceiptCashierLine label={t('cashier')} name={receipt.cashierName} />}
              {/* POS-I229: only for a sale made before receipts were saved; on screen, not on paper */}
              {receipt.tenant?.snapshotBackfilled && receipt.tenant.snapshotTakenAt && (
                <p className="mt-1 text-[11px] text-muted-foreground print:hidden">
                  {t('snapshotBackfilledNote', { date: formatBackfillDate(receipt.tenant.snapshotTakenAt, receipt.tenant.timezone) })}
                </p>
              )}
            </div>

            {/* Customer */}
            {layout.showCustomer && receipt.customer && (
              <div className="text-xs border-b pb-2">
                <p>{t('customer')}: {receipt.customer.name}</p>
                {receipt.customer.phone && <p>{t('phone')}: {receipt.customer.phone}</p>}
                {/* POS-I55: a registered buyer needs its NTN (or CNIC) and ATL status on the invoice to claim input tax. */}
                {receipt.customer.ntn && <p>{t('buyerNtn')}: {receipt.customer.ntn}</p>}
                {!receipt.customer.ntn && receipt.customer.cnic && <p>{t('buyerCnic')}: {receipt.customer.cnic}</p>}
                {(receipt.buyerIsOnAtl || receipt.customer.ntn || receipt.customer.cnic) && (
                  <p>{t('buyerAtlStatus')}: {receipt.buyerIsOnAtl ? t('buyerAtl') : t('buyerNonAtl')}</p>
                )}
              </div>
            )}

            {/* Items */}
            <div className="space-y-1 text-xs">
              {receipt.items.map((item) => {
                const itemTaxBreakdown = normalizeTaxBreakdown(item.taxBreakdown);

                return (
                  <div key={item.id} className="border-b border-dashed border-border py-1 last:border-b-0">
                    <div className="flex justify-between gap-3">
                      <div className="min-w-0 flex-1 break-words">
                        <span>{item.productName}</span>
                        <span className="text-muted-foreground ms-1">
                          x{item.quantity}{item.uom ? ` ${item.uom}` : ''} @ {formatCurrency(Number(item.unitPrice))}
                        </span>
                      </div>
                      <span className="whitespace-nowrap">{formatCurrency(Number(item.totalAmount))}</span>
                    </div>
                    {item.serialNumber && (
                      <div className="ps-2 text-[11px] text-muted-foreground">
                        {serialReceiptLines(
                          item.serialNumber,
                          { serialNo: t('serialNo'), imei: t('imei'), warrantyUntil: t('warrantyUntil') },
                          formatDate,
                        ).map((line) => (
                          <p key={line.key} className={line.mono ? 'font-mono' : undefined}>
                            {line.label}: {line.value}
                          </p>
                        ))}
                      </div>
                    )}
                    {Number(item.discountAmount || 0) > 0 && (
                      <div className="flex justify-between gap-3 ps-2 text-[11px] text-success-text">
                        <span>
                          {t('lineDiscount')}
                          {item.discountType === 'PERCENTAGE' ? ` (${Number(item.discountValue)}%)` : ''}
                        </span>
                        <span>-{formatCurrency(Number(item.discountAmount))}</span>
                      </div>
                    )}
                    {layout.showTaxBreakdown && Number(item.taxAmount || 0) > 0 && (
                      <div className="mt-0.5 space-y-0.5 ps-2 text-[11px] text-muted-foreground">
                        {itemTaxBreakdown.length > 0 ? (
                          itemTaxBreakdown.map((tax, index) => (
                            <div key={`${item.id}-${tax.name}-${index}`} className="flex justify-between gap-3">
                              <span>{formatTaxLabel(tax)}</span>
                              <span>{formatCurrency(tax.amount)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between gap-3">
                            <span>{t('tax')}</span>
                            <span>{formatCurrency(Number(item.taxAmount))}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="border-t pt-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t('subtotal')}</span>
                <span>{formatCurrency(Number(receipt.subtotal))}</span>
              </div>
              {Number(receipt.discountAmount) > 0 && (
                <div className="flex justify-between text-success-text">
                  <span>{t('discount')}</span>
                  <span>-{formatCurrency(Number(receipt.discountAmount))}</span>
                </div>
              )}
              {(receipt.salePromotions?.length ?? 0) > 0 && (
                <>
                  {(receipt.salePromotions ?? []).map((sp) => (
                    <div key={sp.id} className="flex justify-between text-success-text">
                      <span className="truncate max-w-[60%]">🏷 {sp.promotion.name}</span>
                      <span>-{formatCurrency(Number(sp.discountAmount))}</span>
                    </div>
                  ))}
                </>
              )}
              {Number(receipt.loyaltyDiscountAmount || 0) > 0 && (
                <div className="flex justify-between text-success-text">
                  <span>Loyalty points redeemed</span>
                  <span>-{formatCurrency(Number(receipt.loyaltyDiscountAmount))}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>{t('tax')}</span>
                <span>{formatCurrency(Number(receipt.taxAmount))}</span>
              </div>
              {layout.showTaxBreakdown && receiptTaxBreakdown.map((tax) => (
                <div key={`${tax.name}-${tax.rate}-${tax.type}`} className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTaxLabel(tax)}</span>
                  <span>{formatCurrency(tax.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-base border-t pt-1">
                <span>{t('total')}</span>
                <span>{formatCurrency(Number(receipt.totalAmount))}</span>
              </div>
              {/* POS-I359: the till currency: the same total in the currency the customer paid, at the rate stored with the sale */}
              {totalInLine && (
                <div className="flex justify-between text-xs">
                  <span>{t('totalInCurrency', { code: totalInLine.code })} (1 {totalInLine.code} = Rs {formatRate(totalInLine.rate)})</span>
                  <bdi dir="ltr">{formatMoney2(convertPkrToCurrency(receipt.totalAmount, totalInLine.rate), totalInLine.code)}</bdi>
                </div>
              )}
              {/* POS-I119: what the customer saved: line (bargaining) + order + promotion discounts */}
              {youSaved > 0 && (
                <div className="flex justify-between text-sm font-medium text-success-text">
                  <span>{t('youSaved')}</span>
                  <span>{formatCurrency(youSaved)}</span>
                </div>
              )}
              {Number(receipt.tipAmount || 0) > 0 && (
                <div className="flex justify-between text-sm font-medium">
                  <span>Tip / Gratuity</span>
                  <span>{formatCurrency(Number(receipt.tipAmount))}</span>
                </div>
              )}
              {Number(receipt.tipAmount || 0) > 0 && (
                <div className="flex justify-between font-bold text-base border-t pt-1">
                  <span>{t('grandTotal')}</span>
                  <span>{formatCurrency(Number(receipt.totalAmount) + Number(receipt.tipAmount))}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span>{t('paidLabel')}</span>
                <span>{formatCurrency(Number(receipt.paidAmount) - onAccountAmount)}</span>
              </div>
              {onAccountAmount > 0 && (
                <div className="flex justify-between text-xs font-semibold">
                  <span>{t('onAccountBalanceDue')}</span>
                  <span>{formatCurrency(onAccountAmount)}</span>
                </div>
              )}
              {receipt.currencyCode && Number(receipt.foreignAmount || 0) > 0 && (
                <div className="flex justify-between text-xs">
                  <span>{formatCurrency(Number(receipt.foreignAmount), receipt.currencyCode)} @ {new Intl.NumberFormat('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(receipt.exchangeRate || 0))}</span>
                  <span>{formatCurrency(Number(receipt.foreignAmount) * Number(receipt.exchangeRate || 0))}</span>
                </div>
              )}
              {Number(receipt.changeAmount) > 0 && (
                <div className="flex justify-between text-xs">
                  <span>{t('change')}</span>
                  <span>{formatCurrency(Number(receipt.changeAmount))}</span>
                </div>
              )}
            </div>

            {/* Payment method */}
            <div className="border-t pt-2 text-xs text-muted-foreground">
              {receipt.payments.map((p) => (
                <p key={p.id}>
                  {paymentMethodLabel(p.method)}: {formatCurrency(Number(p.amount))}
                  {p.reference && ` (Ref: ${p.reference})`}
                </p>
              ))}
            </div>

            {/* POS-I101: loyalty points of this sale and the balance right after it */}
            {layout.showLoyalty && receipt.loyalty && (receipt.loyalty.pointsEarned > 0 || receipt.loyalty.pointsRedeemed > 0) && (
              <div className="border-t pt-2 text-xs space-y-0.5">
                {receipt.loyalty.pointsRedeemed > 0 && (
                  <div className="flex justify-between">
                    <span>{t('loyaltyPointsRedeemed')}</span>
                    <span>-{receipt.loyalty.pointsRedeemed.toLocaleString()}</span>
                  </div>
                )}
                {receipt.loyalty.pointsEarned > 0 && (
                  <div className="flex justify-between">
                    <span>{t('loyaltyPointsEarned')}</span>
                    <span>+{receipt.loyalty.pointsEarned.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium">
                  <span>{t('loyaltyPointsBalance')}</span>
                  <span>{receipt.loyalty.balanceAfter.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-muted-foreground border-t pt-3">
              <ReceiptFooterText footer={receiptFooter} fallback={t('thankYou')} />
              <ReceiptReturnPolicy text={layout.returnPolicy} />
              {!layout.showFbrQr ? null : showFbrQr ? (
                <div className="flex flex-col items-center gap-2 mt-2">
                  <QRCodeSVG
                    value={[
                      `INV:${resolvedFbrId}`,
                      `AMT:${Number(receipt?.totalAmount ?? 0).toFixed(2)}`,
                      `TAX:${Number(receipt?.taxAmount ?? 0).toFixed(2)}`,
                    ].join('|')}
                    size={112}
                    marginSize={2}
                    className="receipt-qr rounded"
                  />
                  <div className="flex items-center justify-center gap-1.5 text-success-text">
                    <CheckCircle2 className="h-3 w-3" />
                    <span className="receipt-fbr-id font-mono text-muted-foreground">FBR: {resolvedFbrId}</span>
                  </div>
                </div>
              ) : canUseFbr && fbrLive && fbrPollingActive ? (
                (() => {
                  const submission = fbrData?.data?.[0];
                  const fbrStatus = submission?.status;
                  if (fbrStatus === 'FAILED') {
                    return (
                      <div className="mt-1 flex items-center justify-center gap-1 text-destructive-text">
                        <AlertCircle className="h-3 w-3" />
                        <span className="text-xs">{t('fbrFailed')}</span>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-1 flex items-center justify-center gap-1 text-warning-text">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span className="text-xs">{t('fbrSubmitting')}</span>
                    </div>
                  );
                })()
              ) : canUseFbr && fbrLive && fbrPollTimeout ? (
                <p className="mt-1 text-muted-foreground text-xs">{t('fbrPending')}</p>
              ) : null}
            </div>
            {/* POS-I240: product identity, outside every layout.* condition and the receipt snapshot. */}
            <PoweredBy />
          </div>
        )}
      </div>
    </Dialog>
  );
}

